import React, { useState, useEffect, useCallback, useRef } from 'react';
import useInView from '../Animation/useInView.ts';
import styles from './ImageSlider.module.css';

interface ImageSliderProps {
    images: string[];
    title?: string;
    subtitle?: string;
    className?: string;
}

const ImageSlider: React.FC<ImageSliderProps> = ({
    images,
    title,
    subtitle,
    className = ''
}) => {
    const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [showImage, setShowImage] = useState(true);
    const intervalRef = useRef<NodeJS.Timeout>();

    const nextImage = useCallback(() => {
        if (images.length <= 1) return;
        setShowImage(false);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
            setShowImage(true);
        }, 150);
    }, [images.length]);

    const prevImage = useCallback(() => {
        if (images.length <= 1) return;
        setShowImage(false);
        setTimeout(() => {
            setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
            setShowImage(true);
        }, 150);
    }, [images.length]);

    const goToSlide = useCallback((index: number) => {
        if (images.length <= 1) return;
        setShowImage(false);
        setTimeout(() => {
            setCurrentIndex(index);
            setShowImage(true);
        }, 150);
    }, [images.length]);

    useEffect(() => {
        if (!isHovered && images.length > 1) {
            intervalRef.current = setInterval(nextImage, 5000);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isHovered, images.length, nextImage]);

    if (!images.length) return null;

    return (
        <section ref={ref} className={`${styles.sliderSection} ${className} ${isInView ? styles.visible : ''}`}>
            <div className={styles.container}>
                {(title || subtitle) && (
                    <div className={styles.header}>
                        {title && <h2 className={styles.title}>{title}</h2>}
                        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
                    </div>
                )}
                <div
                    className={`${styles.slider} ${isInView ? styles.sliderVisible : ''}`}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    <div className={styles.imageContainer}>
                        <img
                            src={images[currentIndex]}
                            alt={`Slide ${currentIndex + 1}`}
                            className={`${styles.mainImage} ${showImage ? styles.fadeIn : styles.fadeOut}`}
                        />
                        <div className={styles.overlay} />
                    </div>
                    {images.length > 1 && (
                        <>
                            <button className={`${styles.navButton} ${styles.prevButton}`} onClick={prevImage} aria-label="Предыдущее изображение">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                            <button className={`${styles.navButton} ${styles.nextButton}`} onClick={nextImage} aria-label="Следующее изображение">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </>
                    )}
                    {images.length > 1 && (
                        <div className={styles.dots}>
                            {images.map((_, index) => (
                                <button
                                    key={index}
                                    className={`${styles.dot} ${index === currentIndex ? styles.activeDot : ''}`}
                                    onClick={() => goToSlide(index)}
                                    aria-label={`Перейти к изображению ${index + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default ImageSlider;
